// @ts-check
import React from 'react';
import PropTypes from 'prop-types';

import { TestableCard, TestableText } from '@components/general/TestableComponents';
import { convertDateToLocal, formatDuration } from '@datahooks/metadata/DataUtils';

import RagBadge from '../shared/RagBadge';
import { formatCurrency, humaniseValue } from '../shared/businessViewConfig';

/**
 * One figure in the strip along the bottom of the card. Values are separated by a
 * rule rather than a border so the strip reads as a single row.
 * @param {{ testKey: string, label: string, value: React.ReactNode }} props
 */
const SummaryStat = ({ testKey, label, value }) => (
  <div className="min-w-0 flex-1 px-6 py-4">
    <TestableText dataTestId={`bv-flow-stat-label-${testKey}`} className="uppercase tracking-wide">
      {label}
    </TestableText>
    <TestableText
      dataTestId={`bv-flow-stat-${testKey}`}
      variant="h5"
      className="mt-1 normal-case text-blue-500"
    >
      {value}
    </TestableText>
  </div>
);

SummaryStat.propTypes = {
  testKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

/**
 * Header card for a single flow: its key and age, name, description and RAG
 * status, over a strip of the figures that describe the flow itself
 * (criticality, cost, how many tests are linked and of what type).
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').BusinessViewWorkflowDetail} props.workflow
 * @param {{ testTypeId: number, label: string, count: number }[]} [props.coverageMix]
 * @param {number|null} [props.dailyCost] Cost per day, from resolveDailyCost.
 * @returns {JSX.Element}
 */
const BusinessViewFlowSummaryCard = ({ workflow, coverageMix = [], dailyCost }) => {
  const { workflowKey, name, description, criticality, riskStatus, totalTests, calculatedAt } =
    workflow || {};

  const coverageLabel = coverageMix.length
    ? coverageMix.map((entry) => `${entry.count} ${entry.label}`).join(' · ')
    : '—';

  // p-0 because this card manages its own padding: the rules have to span its full width.
  return (
    <TestableCard dataTestId="bv-flow-summary-card" className="overflow-hidden p-0 shadow-sm">
      <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <TestableText dataTestId="bv-flow-meta" variant="small" className="flex flex-wrap items-center gap-2 text-gray-600 dark:text-gray-300">
            <span data-testid="bv-flow-key">{workflowKey}</span>
            {calculatedAt && (
              <>
                <span aria-hidden="true" className="text-gray-400">|</span>
                {/*
                  The calculation is only as current as the run behind it, so its age
                  is shown rather than left to be assumed, with the exact time on hover.
                */}
                <span data-testid="bv-flow-calculated-at" title={convertDateToLocal(calculatedAt, true, true, false)}>
                  Last updated {formatDuration(calculatedAt)} ago
                </span>
              </>
            )}
          </TestableText>

          <TestableText dataTestId="bv-flow-name" variant="h3" className="mt-2 normal-case">
            {name}
          </TestableText>

          {description && (
            <TestableText dataTestId="bv-flow-description" variant="small" className="mt-2 text-gray-600 dark:text-gray-300">
              {description}
            </TestableText>
          )}
        </div>

        <div className="shrink-0">
          <RagBadge status={riskStatus || 'unavailable'} testId="bv-flow-rag" />
        </div>
      </div>

      <hr className="border-gray-300 dark:border-gray-700" />

      <div className="flex flex-col p-2 divide-y divide-gray-300 sm:flex-row sm:divide-x sm:divide-y-0 dark:divide-gray-700">
        <SummaryStat
          testKey="criticality"
          label="Criticality"
          value={criticality ? humaniseValue(criticality) : '—'}
        />
        <SummaryStat
          testKey="daily-risk-cost"
          label="Daily Risk Cost"
          /*
            Null means the flow has no cost figure at all, which is not the same as
            costing nothing. A flow that is not currently at risk shows its configured
            impact rather than £0 — see resolveDailyCost.
          */
          value={
            dailyCost == null ? (
              '—'
            ) : (
              <span className="flex items-baseline gap-1">
                {formatCurrency(dailyCost)}
                <span className="text-sm font-light text-gray-600 dark:text-gray-300">/ day</span>
              </span>
            )
          }
        />
        <SummaryStat testKey="linked-tests" label="Linked Tests" value={totalTests ?? '—'} />
        <SummaryStat testKey="coverage-mix" label="Coverage Mix" value={coverageLabel} />
      </div>
    </TestableCard>
  );
};

BusinessViewFlowSummaryCard.propTypes = {
  workflow: PropTypes.shape({
    workflowKey: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    criticality: PropTypes.string,
    riskStatus: PropTypes.string,
    totalTests: PropTypes.number,
    calculatedAt: PropTypes.string,
  }).isRequired,
  dailyCost: PropTypes.number,
  coverageMix: PropTypes.arrayOf(
    PropTypes.shape({
      testTypeId: PropTypes.number,
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ),
};

export default BusinessViewFlowSummaryCard;
